import React, { useRef, useState } from 'react';
import { 
  Button, 
  Box, 
  TextField, 
  Grid, 
  Typography,
  InputLabel,
  FormControl
} from '@mui/material';
import AkMoInvoicingFormat, { 
  BusinessDetails, 
  ClientDetails,
  DeliveryDetails,
  InvoiceDetails,
  InvoiceItem
} from '../components/templates/AkMoInvoicingFormat';
import { exportInvoiceToPdf } from '../utils/invoicePdfExporter';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const InvoiceDemo: React.FC = () => {
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [primaryColor, setPrimaryColor] = useState('#4776E6');
  const [secondaryColor, setSecondaryColor] = useState('#8E54E9');
  const [fontColor, setFontColor] = useState('#333333');
  const [isExporting, setIsExporting] = useState(false);
  
  // Sample business details
  const businessDetails: BusinessDetails = {
    name: "AkMo Technologies Pvt. Ltd.",
    address: "123 Main Street, Tech Park",
    city: "Bangalore",
    state: "Karnataka",
    pincode: "560001",
    gstin: "29AABCA1234D1Z5",
    phone: "+91 9876543210",
    email: "accounts@akmotech.com",
    bankName: "HDFC Bank",
    bankBranch: "Koramangala Branch",
    accountNumber: "123456789012345",
    ifsc: "HDFC0001234",
    upiId: "accounts@akmotech",
    gstRegistered: "registered",
    gstType: "regular"
  };

  // Sample client details
  const clientDetails: ClientDetails = {
    name: "ABC Enterprises",
    address: "456 Business Avenue",
    city: "Mumbai",
    state: "Maharashtra",
    pincode: "400001",
    gstin: "27AADCS1234A1Z9",
    phone: "+91 9876543211",
    email: "info@abcenterprises.com"
  };

  // Sample delivery details
  const deliveryDetails: DeliveryDetails = {
    name: "ABC Enterprises - Warehouse",
    address: "789 Industrial Zone",
    city: "Mumbai",
    state: "Maharashtra",
    pincode: "400003",
    phone: "+91 9876543212",
    email: "warehouse@abcenterprises.com",
    contactPerson: "Rajesh Kumar",
    contactNumber: "+91 9876543213"
  };

  // Sample invoice details
  const invoiceDetails: InvoiceDetails = {
    invoiceNumber: "INV-2023-0001",
    invoiceDate: "05/04/2023",
    dueDate: "20/04/2023",
    purchaseRefNumber: "PO-2023-0123",
    discount: 2000,
    additionalCharges: [
      { name: "Shipping", amount: 500 },
      { name: "Packaging", amount: 300 }
    ],
    termsAndConditions: [
      "Payment to be made within 15 days from the date of invoice.",
      "Goods once sold cannot be returned or exchanged.",
      "Interest at 18% p.a. will be charged on overdue payments.",
      "All disputes are subject to Bangalore jurisdiction only."
    ]
  };

  // Sample invoice items
  const invoiceItems: InvoiceItem[] = [
    {
      productName: "Web Development Services",
      description: "E-commerce website development including payment gateway integration",
      quantity: 1,
      unit: "Project",
      hsn: "998314",
      gstRate: 18,
      taxableValue: 50000,
      taxAmount: 9000,
      total: 59000
    },
    {
      productName: "Server Hosting (Annual)",
      description: "Cloud server hosting with daily backups",
      quantity: 1,
      unit: "Year",
      hsn: "998316",
      gstRate: 18,
      taxableValue: 12000,
      taxAmount: 2160,
      total: 14160
    },
    {
      productName: "UI/UX Design",
      description: "Custom design for mobile app",
      quantity: 1,
      unit: "Service",
      hsn: "998391",
      gstRate: 18,
      taxableValue: 25000,
      taxAmount: 4500,
      total: 29500
    },
    {
      productName: "SSL Certificate (Premium)",
      quantity: 3,
      unit: "Years",
      hsn: "998399",
      gstRate: 18,
      taxableValue: 6000,
      taxAmount: 1080,
      total: 7080
    }
  ];

  const handleExportPdf = async () => {
    if (invoiceRef.current) {
      try {
        setIsExporting(true);
        
        // Create a document with custom styling
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'pt',
          format: 'a4',
        });
        
        // Define styling based on selected colors
        const styles = {
          primaryColor: primaryColor,
          secondaryColor: secondaryColor,
          fontColor: fontColor,
          gradientBackground: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
        };
        
        // Clone the invoice element to modify for PDF export
        const invoiceElement = invoiceRef.current.cloneNode(true) as HTMLElement;
        
        // Apply custom styles for PDF
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
          .invoice-header { 
            background: ${styles.gradientBackground} !important;
            -webkit-print-color-adjust: exact;
          }
          .invoice-body { color: ${styles.fontColor} !important; }
          .invoice-total { color: ${styles.primaryColor} !important; }
        `;
        invoiceElement.appendChild(styleElement);
        
        // Convert to PDF
        const canvas = await html2canvas(invoiceRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = doc.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        doc.save('invoice.pdf');
      } catch (error) {
        console.error('Error exporting PDF:', error);
      } finally {
        setIsExporting(false);
      }
    }
  };

  const resetColors = () => {
    setPrimaryColor('#4776E6');
    setSecondaryColor('#8E54E9');
    setFontColor('#333333');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ maxWidth: { xs: '100%', md: '70%' } }}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Customize Colors:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel shrink htmlFor="primary-color">Primary Color</InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  style={{ width: '40px', height: '30px', marginRight: '8px', border: 'none', borderRadius: '4px' }}
                />
                <TextField 
                  id="primary-color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  size="small"
                  sx={{ width: '90px' }}
                />
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel shrink htmlFor="secondary-color">Secondary Color</InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  style={{ width: '40px', height: '30px', marginRight: '8px', border: 'none', borderRadius: '4px' }}
                />
                <TextField 
                  id="secondary-color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  size="small"
                  sx={{ width: '90px' }}
                />
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel shrink htmlFor="font-color">Font Color</InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <input
                  type="color"
                  value={fontColor}
                  onChange={(e) => setFontColor(e.target.value)}
                  style={{ width: '40px', height: '30px', marginRight: '8px', border: 'none', borderRadius: '4px' }}
                />
                <TextField 
                  id="font-color"
                  value={fontColor}
                  onChange={(e) => setFontColor(e.target.value)}
                  size="small"
                  sx={{ width: '90px' }}
                />
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={resetColors}
              sx={{ mt: 1 }}
            >
              Reset Colors
            </Button>
          </Grid>
        </Grid>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleExportPdf}
          sx={{ 
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            alignSelf: { xs: 'flex-start', md: 'center' },
            minWidth: '150px'
          }}
        >
          Export as PDF
        </Button>
      </Box>
      
      <div id="invoice-template" ref={invoiceRef}>
        <AkMoInvoicingFormat
          businessDetails={businessDetails}
          clientDetails={clientDetails}
          deliveryDetails={deliveryDetails}
          invoiceDetails={invoiceDetails}
          items={invoiceItems}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          fontColor={fontColor}
        />
      </div>
    </Box>
  );
};

export default InvoiceDemo; 